import type { FC } from "react";

import { Column, Row } from "@hightouch/htui";
import round from "lodash/round";
import type { TooltipProps } from "recharts";

import {
  type FunnelStepGraphDataPoint,
  type HoveredSection,
  FunnelMeasurementType,
  type FunnelStepGraphData,
} from "src/pages/analytics/types";
import { EMPTY_AUDIENCE_NAME } from "src/pages/analytics/utils";
import { chartBaseColors } from "src/pages/analytics/constants";
import {
  ChartTooltipValue,
  ChartTooltipWrapper,
  ChartTooltipColor,
  ChartTooltipTitle,
  ChartTooltipSubtitle,
} from "src/components/charts/chart-tooltip";

type Props = {
  funnelMeasuringType: FunnelMeasurementType;
  hoveredSection: HoveredSection;
} & TooltipProps<number, string>;

function TooltipRow({
  value,
  description,
}: {
  value: string | number;
  description: string;
}) {
  return <ChartTooltipValue value={{ value, description }} />;
}

const Tooltip: FC<Readonly<Props>> = ({
  hoveredSection,
  funnelMeasuringType,
  payload,
}) => {
  if (!payload || !hoveredSection.dataKey) return null;

  const getSolidColorFromGradient = (
    seriesName: string,
    funnelData: FunnelStepGraphData,
  ) => {
    // Extract the solid color that was used to create the gradient
    const colorIndex = Object.keys(funnelData.data ?? {}).indexOf(seriesName);
    return chartBaseColors[(colorIndex + 3) % chartBaseColors.length]?.color;
  };

  return (
    <ChartTooltipWrapper>
      {payload.map(({ payload: funnel, color, name }, index) => {
        const item: FunnelStepGraphDataPoint | undefined =
          funnel.data[hoveredSection.seriesName ?? ""];

        if (!item) return null;

        const solidColor = color?.startsWith("url(")
          ? getSolidColorFromGradient(hoveredSection.seriesName ?? "", funnel)
          : color;

        return hoveredSection.dataKey === name ? (
          <Row gap={2} key={index}>
            {solidColor && <ChartTooltipColor color={solidColor} />}
            <Column gap={1}>
              <ChartTooltipTitle>
                {hoveredSection.section === "conversion"
                  ? "Conversion"
                  : "Drop-off"}
              </ChartTooltipTitle>
              {hoveredSection.seriesName &&
                hoveredSection.seriesName !== EMPTY_AUDIENCE_NAME && (
                  <ChartTooltipSubtitle>
                    {hoveredSection.seriesName}
                  </ChartTooltipSubtitle>
                )}
              <TooltipRow
                value={`${
                  hoveredSection.section === "conversion"
                    ? round(item.conversion * 100, 2)
                    : round((1 - item.conversion) * 100, 2)
                }%`}
                description={
                  hoveredSection.section === "conversion"
                    ? "conversion"
                    : "drop-off"
                }
              />
              <TooltipRow
                value={
                  hoveredSection.section === "conversion"
                    ? item.count.toLocaleString()
                    : item.conversion === 0
                      ? 0
                      : Math.round(
                          item.count / item.conversion - item.count,
                        ).toLocaleString()
                }
                description={
                  funnelMeasuringType === FunnelMeasurementType.TotalConversion
                    ? "events"
                    : "unique customers"
                }
              />
            </Column>
          </Row>
        ) : null;
      })}
    </ChartTooltipWrapper>
  );
};

export default Tooltip;
